import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { selectCurrentTeam, selectCurrentTeamJobPermissions, selectIsActionsBlocked } from 'containers/app/appSlice';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';
import { useLazyGetServiceAreasQuery } from 'containers/settings/service-areas/api';
import { useAppSelector } from 'hooks';
import routesPath from 'routes/RoutesPath';
import {
  preloadDispatchMap,
  resetDispatchMapPreloadState,
  scheduleDispatchMapPreload
} from 'utils/maps/preloadDispatchMap';

/**
 * Preloads dispatch map JS chunks, Google Maps libraries, and service area data in the background
 * for active FSM Plus teams, before the user navigates to /maps.
 */
export const usePreloadDispatchMap = () => {
  const location = useLocation();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const viewJobSettings = useAppSelector(selectCurrentTeamJobPermissions)?.viewJobSettings;
  const { isActiveFSMPlusPlan } = useFSMPlanInfo();
  const [getServiceAreas] = useLazyGetServiceAreasQuery();
  const preloadedTeamIdRef = useRef<number | null>(null);
  const cancelPreloadRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    cancelPreloadRef.current?.();
    cancelPreloadRef.current = null;

    const teamId = currentTeam?.id;
    const isOnMapsPage = location.pathname.startsWith(routesPath.MAPS);
    const shouldPreload = Boolean(
      isActiveFSMPlusPlan && teamId && !isActionsBlocked && !isOnMapsPage && viewJobSettings
    );

    if (!shouldPreload) {
      if (preloadedTeamIdRef.current !== null && preloadedTeamIdRef.current !== teamId) {
        resetDispatchMapPreloadState();
        preloadedTeamIdRef.current = null;
      }
      return;
    }

    if (preloadedTeamIdRef.current === teamId) {
      return;
    }

    if (preloadedTeamIdRef.current !== null && preloadedTeamIdRef.current !== teamId) {
      resetDispatchMapPreloadState();
    }

    preloadedTeamIdRef.current = teamId ?? null;

    cancelPreloadRef.current = scheduleDispatchMapPreload(() => {
      preloadDispatchMap(teamId, getServiceAreas);
    });

    return () => {
      cancelPreloadRef.current?.();
      cancelPreloadRef.current = null;
    };
  }, [currentTeam?.id, getServiceAreas, isActionsBlocked, isActiveFSMPlusPlan, location.pathname, viewJobSettings]);
};
